import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { CreateMemoryGameProcessedContentExampleTemplate } from '../../components/Templates';
import { UiLocale } from '../../models/Base';
import {
  getQuickCreateGameModeProps,
  QuickCreateGameMode,
} from '../../models/DraftStore';
import { useDraftStore } from '../../stores/DraftStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { uiToTranslation } from '../../utils/i18n';

interface State {
  createType: QuickCreateGameMode | undefined;
}

const CreateMemoryGameProcessedContentExampleScreen: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const navigationState = location.state as {
    withBackState: boolean;
  } | null;

  const [state, setState] = useState<State>({
    createType: undefined,
  });

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userAccount = useUserStore((store) =>
    store.getAccount(activeUserId ?? ''),
  );

  const { mode, locale } = useDraftStore((store) => {
    const _mode = store.getGameMode();
    const _locale = store.getLocale();

    return { mode: _mode, locale: _locale };
  });

  useEffect(() => {
    if (mode) {
      setState((s) => ({ ...s, createType: mode }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [mode, navigate]);

  useEffect(() => {
    i18n
      .loadLanguages([uiToTranslation(locale as UiLocale)])
      .catch(console.error)
      .finally(() => {
        setState((s) => ({ ...s, rerednerState: true }));
      });
  }, [i18n, locale]);

  const onClickBack = useCallback(() => {
    if (navigationState?.withBackState) {
      navigate(-1);
    } else {
      navigate('/create/memory-game/input-content');
    }
  }, [navigate, navigationState?.withBackState]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
      e.preventDefault();
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <CreateMemoryGameProcessedContentExampleTemplate
      type={state.createType}
      webBoosterType={userAccount?.settings.webBoosterType}
      selectedLanguage={locale}
      gameContents={
        mode
          ? getQuickCreateGameModeProps(
              mode,
              userAccount?.settings.webBoosterType,
            ).exampleContents
          : []
      }
      onClickBack={onClickBack}
    />
  );
};
export default CreateMemoryGameProcessedContentExampleScreen;
